import { DashboardStatsResponse } from '@clareefai/types';
import { Avatar } from '@clareefai/ui';
import {
  Award,
  BarChart3,
  Calendar,
  DollarSign,
  Euro,
  FileText, Globe,
  Phone,
  Star,
  Target,
  TrendingUp,
  UserCheck,
  Users,
  Video
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface DashboardAnalyticsProps {
  stats: DashboardStatsResponse | undefined;
  loading: boolean;
}

const DashboardAnalytics: React.FC<DashboardAnalyticsProps> = ({ stats, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="space-y-8">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const formatChange = (change: number) => {
    const sign = change >= 0 ? '+' : '';
    return t('dashboard.analytics.vsLastMonth', { sign, value: change.toFixed(1) });
  };

  // Performance Statistics - using real data
  const performanceStats = [
    {
      title: t('dashboard.analytics.readyForCall'),
      value: stats.readyForCallPromoters.toLocaleString(),
      change: t('dashboard.analytics.promotersReady', { count: stats.readyForCallPromoters }),
      icon: Phone,
      color: 'bg-indigo-50 text-indigo-600'
    },
    {
      title: t('dashboard.analytics.totalRevenue'),
      value: t('dashboard.analytics.comingSoon'),
      change: '',
      icon: DollarSign,
      color: 'bg-emerald-50 text-emerald-600',
      comingSoon: true
    },
    {
      title: t('dashboard.analytics.pendingTestimonials'),
      value: stats.pendingTestimonials.toLocaleString(),
      change: t('dashboard.analytics.pendingReview', { count: stats.pendingTestimonials }),
      icon: FileText,
      color: 'bg-amber-50 text-amber-600'
    },
    {
      title: t('dashboard.analytics.conversionRate'),
      value: t('dashboard.analytics.comingSoon'),
      change: '',
      icon: Target,
      color: 'bg-green-50 text-green-600',
      comingSoon: true
    }
  ];

  // Top Performers - using real data
  const topPerformersList = stats.topPerformers.map((performer, index) => ({
    rank: index + 1,
    name: performer.promoterName,
    company: performer.promoterEmail || t('common.notAvailable'),
    avatar: performer.promoterAvatar || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(performer.promoterName),
    amount: performer.totalRewards,
    conversions: performer.totalProspects,
    conversionRate: performer.conversionRate,
    meetings: performer.totalMeetings,
    prospectsReferred: performer.prospectsReferred
  }));

  // Rewards by Type - using real data
  const rewardsByTypeList = stats.rewardsByType.map((reward) => ({
    type: reward.type.replace('_', ' '),
    count: reward.count,
    amount: reward.totalAmount,
    color: reward.type === 'REVIEW' ? 'bg-yellow-500' :
      reward.type === 'VIDEO' ? 'bg-red-500' :
        reward.type === 'REFERRAL' ? 'bg-blue-500' :
          'bg-green-500'
  }));

  // Additional analytics - calculate from rewards by type
  const reviewRewards = stats.rewardsByType.find(r => r.type === 'REVIEW');
  const videoRewards = stats.rewardsByType.find(r => r.type === 'VIDEO');
  const referralRewards = stats.rewardsByType.find(r => r.type === 'REFERRAL');

  const additionalMetrics = [
    {
      title: t('dashboard.analytics.totalReviews'),
      value: reviewRewards?.count.toString() || '0',
      change: t('dashboard.analytics.reviewsCount', { count: reviewRewards?.count || 0 }),
      icon: Star,
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      title: t('dashboard.analytics.videoTestimonials'),
      value: videoRewards?.count.toString() || '0',
      change: t('dashboard.analytics.videosCount', { count: videoRewards?.count || 0 }),
      icon: Video,
      color: 'bg-red-100 text-red-600'
    },
    {
      title: t('dashboard.analytics.referrals'),
      value: referralRewards?.count.toString() || '0',
      change: t('dashboard.analytics.referralsCount', { count: referralRewards?.count || 0 }),
      icon: Users,
      color: 'bg-indigo-100 text-indigo-600'
    },
    {
      title: t('dashboard.analytics.totalRewardsTitle'),
      value: `$${stats.rewardsByType.reduce((sum, r) => sum + r.totalAmount, 0).toLocaleString()}`,
      change: t('dashboard.analytics.allTime'),
      icon: Award,
      color: 'bg-green-100 text-green-600'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Performance Statistics */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <BarChart3 className="w-6 h-6 mr-2 text-blue-600" />
          {t('dashboard.analytics.performanceStats')}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {performanceStats.map((stat, index) => (
            <div key={index} className={`text-center p-6 bg-gray-50 rounded-lg border border-gray-200 ${stat.comingSoon ? 'opacity-60' : ''}`}>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 ${stat.color}`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <div className={`font-bold mb-2 ${stat.comingSoon ? 'text-sm text-gray-400' : 'text-3xl text-gray-900'}`}>{stat.value}</div>
              <div className="text-sm text-gray-600 mb-1">{stat.title}</div>
              {stat.change && <div className="text-xs text-green-600">{stat.change}</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Performers This Month */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Award className="w-5 h-5 mr-2 text-purple-600" />
            {t('dashboard.analytics.topPerformers')}
          </h3>

          <div className="space-y-4">
            {topPerformersList.length > 0 ? topPerformersList.map((performer) => (
              <div key={performer.name} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${performer.rank === 1 ? 'bg-yellow-500' :
                      performer.rank === 2 ? 'bg-gray-400' :
                        performer.rank === 3 ? 'bg-orange-600' :
                          'bg-gray-300'
                    }`}>
                    {performer.rank}
                  </div>
                  <Avatar
                    src={performer.avatar}
                    name={performer.name}
                    size="md"
                  />
                  <div>
                    <h4 className="font-medium text-gray-900">{performer.name}</h4>
                    <p className="text-sm text-gray-600">{performer.company}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-600">${performer.amount.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">{t('dashboard.analytics.prospect', { count: performer.conversions })}</div>
                  <div className="text-xs text-gray-400">{t('dashboard.analytics.meeting', { count: performer.meetings })}</div>
                </div>
              </div>
            )) : (
              <p className="text-center text-gray-500 py-4">{t('dashboard.analytics.noTopPerformers')}</p>
            )}
          </div>
        </div>

        {/* Rewards by Type */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Euro className="w-5 h-5 mr-2 text-green-600" />
            {t('dashboard.analytics.rewardsByType')}
          </h3>

          <div className="space-y-4">
            {rewardsByTypeList.length > 0 ? rewardsByTypeList.map((reward, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full ${reward.color}`}></div>
                  <div>
                    <h4 className="font-medium text-gray-900">{reward.type}</h4>
                    <p className="text-sm text-gray-600">{t('dashboard.analytics.reward', { count: reward.count })}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-gray-900">${reward.amount.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">{t('dashboard.analytics.total')}</div>
                </div>
              </div>
            )) : (
              <p className="text-center text-gray-500 py-4">{t('dashboard.analytics.noRewardsData')}</p>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-900">{t('dashboard.analytics.totalRewards')}</span>
              <span className="text-xl font-bold text-green-600">
                ${stats.rewardsByType.reduce((sum, r) => sum + r.totalAmount, 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Additional Metrics */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-indigo-600" />
            {t('dashboard.analytics.keyMetrics')}
          </h3>

          <div className="grid grid-cols-2 gap-4">
            {additionalMetrics.map((metric, index) => (
              <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3 ${metric.color}`}>
                  <metric.icon className="w-5 h-5" />
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">{metric.value}</div>
                <div className="text-sm text-gray-600 mb-1">{metric.title}</div>
                <div className="text-xs text-green-600">{metric.change}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Promoters Ready for Reference Call */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Phone className="w-5 h-5 mr-2 text-green-600" />
            {t('dashboard.analytics.readyForReferenceCall', { count: stats.readyForCallPromoters })}
          </h3>

          <div className="space-y-3">
            {topPerformersList.filter(p => p.conversionRate > 0).slice(0, 5).map((promoter) => (
              <div key={promoter.name} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center space-x-3">
                  <Avatar
                    src={promoter.avatar}
                    name={promoter.name}
                    size="md"
                  />
                  <div>
                    <h4 className="font-medium text-gray-900">{promoter.name}</h4>
                    <p className="text-sm text-gray-600">{promoter.company}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-green-600">{promoter.conversionRate.toFixed(1)}%</div>
                  <div className="text-xs text-gray-500">{t('dashboard.analytics.conversion')}</div>
                </div>
              </div>
            ))}
            {topPerformersList.filter(p => p.conversionRate > 0).length === 0 && (
              <p className="text-center text-gray-500 py-4">{t('dashboard.analytics.noPromotersForCall')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Monthly Trends */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
          {t('dashboard.analytics.monthlyTrends')}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
            <UserCheck className="w-8 h-8 text-blue-600 mx-auto mb-3" />
            <div className="text-2xl font-bold text-blue-900">{stats.monthlyTrends.activePromoters.currentMonth}</div>
            <div className="text-sm text-blue-700">{t('dashboard.analytics.activePromotersLabel')}</div>
            <div className={`text-xs mt-1 ${stats.monthlyTrends.activePromoters.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {t('dashboard.analytics.vsLastMonth', { sign: stats.monthlyTrends.activePromoters.change >= 0 ? '+' : '', value: stats.monthlyTrends.activePromoters.change.toFixed(1) })}
            </div>
          </div>

          <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
            <Target className="w-8 h-8 text-purple-600 mx-auto mb-3" />
            <div className="text-2xl font-bold text-purple-900">{stats.monthlyTrends.prospects.currentMonth}</div>
            <div className="text-sm text-purple-700">{t('dashboard.analytics.newProspects')}</div>
            <div className={`text-xs mt-1 ${stats.monthlyTrends.prospects.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {t('dashboard.analytics.vsLastMonth', { sign: stats.monthlyTrends.prospects.change >= 0 ? '+' : '', value: stats.monthlyTrends.prospects.change.toFixed(1) })}
            </div>
          </div>

          <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
            <Calendar className="w-8 h-8 text-green-600 mx-auto mb-3" />
            <div className="text-2xl font-bold text-green-900">{stats.monthlyTrends.meetings.currentMonth}</div>
            <div className="text-sm text-green-700">{t('dashboard.analytics.meetings')}</div>
            <div className={`text-xs mt-1 ${stats.monthlyTrends.meetings.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {t('dashboard.analytics.vsLastMonth', { sign: stats.monthlyTrends.meetings.change >= 0 ? '+' : '', value: stats.monthlyTrends.meetings.change.toFixed(1) })}
            </div>
          </div>

          <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200 opacity-60">
            <Euro className="w-8 h-8 text-orange-600 mx-auto mb-3" />
            <div className="text-sm font-bold text-gray-400">{t('dashboard.analytics.comingSoon')}</div>
            <div className="text-sm text-orange-700">{t('dashboard.analytics.revenue')}</div>
          </div>
        </div>
      </div>

      {/* Content Performance */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <Globe className="w-5 h-5 mr-2 text-indigo-600" />
          {t('dashboard.analytics.contentPerformance')}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
            <div className="flex items-center justify-between mb-3">
              <Star className="w-8 h-8 text-yellow-600" />
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">{t('dashboard.analytics.featured')}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{stats.contentPerformance.featuredReviews}</div>
            <div className="text-sm text-gray-600 mb-2">{t('dashboard.analytics.featuredReviews')}</div>
            {stats.contentPerformance.featuredReviewsThisMonth > 0 ? (
              <div className="text-xs text-green-600">{t('dashboard.analytics.newThisMonth', { count: stats.contentPerformance.featuredReviewsThisMonth })}</div>
            ) : (
              <div className="text-xs text-gray-500">{t('dashboard.analytics.noNewThisMonth')}</div>
            )}
          </div>

          <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-lg p-4 border border-red-200">
            <div className="flex items-center justify-between mb-3">
              <Video className="w-8 h-8 text-red-600" />
              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-medium">{t('dashboard.analytics.live')}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{stats.contentPerformance.videoTestimonials}</div>
            <div className="text-sm text-gray-600 mb-2">{t('dashboard.analytics.videoTestimonials')}</div>
            {stats.contentPerformance.videoTestimonialsThisMonth > 0 ? (
              <div className="text-xs text-green-600">{t('dashboard.analytics.newThisMonth', { count: stats.contentPerformance.videoTestimonialsThisMonth })}</div>
            ) : (
              <div className="text-xs text-gray-500">{t('dashboard.analytics.noNewThisMonth')}</div>
            )}
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
            <div className="flex items-center justify-between mb-3">
              <FileText className="w-8 h-8 text-indigo-600" />
              <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full font-medium">{t('dashboard.analytics.published')}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{stats.contentPerformance.useCases}</div>
            <div className="text-sm text-gray-600 mb-2">{t('dashboard.analytics.useCases')}</div>
            {stats.contentPerformance.useCasesThisMonth > 0 ? (
              <div className="text-xs text-green-600">{t('dashboard.analytics.newThisMonth', { count: stats.contentPerformance.useCasesThisMonth })}</div>
            ) : (
              <div className="text-xs text-gray-500">{t('dashboard.analytics.noNewThisMonth')}</div>
            )}
          </div>
        </div>
      </div>

      {/* Engagement Metrics - commented out
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <Heart className="w-5 h-5 mr-2 text-red-600" />
          Engagement Metrics
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600 mb-2">342</div>
            <div className="text-sm text-gray-600">Total Likes</div>
            <div className="text-xs text-green-600">+45 this month</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">156</div>
            <div className="text-sm text-gray-600">Shares</div>
            <div className="text-xs text-green-600">+23 this month</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">89</div>
            <div className="text-sm text-gray-600">Comments</div>
            <div className="text-xs text-green-600">+12 this month</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">7.2%</div>
            <div className="text-sm text-gray-600">Engagement Rate</div>
            <div className="text-xs text-green-600">+1.1% this month</div>
          </div>
        </div>
      </div>
      */}
    </div>
  );
};

export default DashboardAnalytics;